/**
 * RoomItems — native staging item step (lifted from
 * `common/StagingFlow/RoomItems/RoomItems.component.tsx`, rewired onto the module
 * working context + `patchRoom` + plain-JS room arrays). Analytics dropped.
 */

import { Dispatch, FC, Fragment, SetStateAction, useCallback, useMemo } from 'react'

import { GRAY_700, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import Stack from '@mui/material/Stack'
import { StagingType } from 'models/virtualStaging'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import Typography from '@mui/material/Typography'
import { VirtualFurnitureRoomRemovableOption } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'

import { DecorationsSelectionPopup } from './DecorationsSelectionPopup.component'
import { FurnitureItemSelectionPopup } from './FurnitureTemplateSelectionPopup.component'
import { RenovationsSelectionPopup } from './RenovationsSelectionPopup.component'
import { RoomItemsButtons } from './RoomItemsButtons.component'
import { VisualStyleSelectionContainerState } from './VisualStyleSelectionsContainer.component'
import { RoomItemPopupType, useStagingWorkingState } from '../StagingWorkingState.context'

interface RoomItemsProps {
  setVisualStyleSelectionStep: Dispatch<SetStateAction<VisualStyleSelectionContainerState>>
}

export const RoomItems: FC<RoomItemsProps> = ({ setVisualStyleSelectionStep }) => {
  const { t } = useTranslation(['style_selection_order_step', 'virtual_furniture_room_type'])

  const {
    activeRoom,
    selectedRemovableObject,
    roomItemPopupType,
    setIsSpecialRequestPopupOpen,
    patchRoom,
  } = useStagingWorkingState()

  const onOpenSpecialRequestPopup = useCallback(() => {
    setIsSpecialRequestPopupOpen(true)
  }, [setIsSpecialRequestPopupOpen])

  const resetRoomItems = useCallback(() => {
    if (!activeRoom) return
    patchRoom(activeRoom.key, {
      styleCodeMap: [],
      decorations: [],
      renovations: [],
      skipRenovations: false,
    })
  }, [activeRoom, patchRoom])

  const backToRoomTypes = useCallback(() => {
    setVisualStyleSelectionStep(VisualStyleSelectionContainerState.ROOM_TYPES_AND_REMOVABLE_OPTIONS)
    resetRoomItems()
  }, [resetRoomItems, setVisualStyleSelectionStep])

  const optionsStagingType: ToggleFilterValue<StagingType>[] = useMemo(
    () =>
      Object.values(StagingType).map((stagingType) => ({
        value: stagingType,
        displayName: t(`style_selection_order_step:staging_type.${stagingType}`),
      })),
    [t]
  )

  const onSelectStagingType = useCallback(
    (item: StagingType) => {
      if (!activeRoom) return
      patchRoom(activeRoom.key, { type: item })
      resetRoomItems()
    },
    [activeRoom, resetRoomItems, patchRoom]
  )

  if (!activeRoom) return null

  return (
    <Stack justifyContent="space-between" alignItems="center" position="relative">

      <MUIButton
        isIconButton
        type="secondaryNoBorder"
        size="xs"
        startIcon={<ArrowBackRoundedIcon />}
        onClick={backToRoomTypes}
        sx={{ position: 'absolute', left: 0, top: 0 }}
      >
        {t('style_selection_order_step:back')}
      </MUIButton>

      <Stack maxWidth="70%" gap={1} direction="row" flexWrap="wrap" justifyContent="center" mb={2}>

        {activeRoom.roomTypes.map((roomType) => (
          <MUIBadge
            label={t(`virtual_furniture_room_type:${roomType}`)}
            key={roomType}
            size="lg"
            color="gray"
            sx={{ backgroundColor: WHITE }}
          />
        ))}

        {selectedRemovableObject && selectedRemovableObject !== VirtualFurnitureRoomRemovableOption.NONE &&
          <MUIBadge
            label={t(`virtual_furniture_room_removable_option:${selectedRemovableObject}`)}
            size="lg"
            color="gray"
            sx={{ backgroundColor: WHITE, marginLeft: 1 }}
          />
        }

      </Stack>

      <Stack alignItems="center" gap={2}>

        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('style_selection_order_step:staging_type_question')}
        </Typography>

        <SelectableButtonsGrid
          values={optionsStagingType}
          selectedValue={activeRoom.type}
          onSelect={onSelectStagingType}
          icons={{
            [StagingType.BKBN]: (
              <MUITooltip content={t('style_selection_order_step:staging_type_tooltip')}>
                <Stack>
                  <InfoOutlinedIcon sx={{ color: GRAY_800, fontSize: '1.8rem' }} />
                </Stack>
              </MUITooltip>
            ),
          }}
        />

      </Stack>

      {activeRoom.type &&
        <Fragment>

          <RoomItemsButtons />

          <FurnitureItemSelectionPopup isOpen={roomItemPopupType === RoomItemPopupType.FURNITURE} />
          <DecorationsSelectionPopup isOpen={roomItemPopupType === RoomItemPopupType.DECORATION} />
          <RenovationsSelectionPopup isOpen={roomItemPopupType === RoomItemPopupType.WALL || roomItemPopupType === RoomItemPopupType.FLOOR} />

          <Stack width="100%" gap=".4rem" flexDirection="row" justifyContent="center" padding="2.4rem 0 1rem 0">

            <Typography variant="text-sm" color={GRAY_700}>
              {t('style_selection_order_step:special_request.need_special_request')}
            </Typography>

            <Typography
              variant="text-sm"
              fontWeight={600}
              color={GRAY_900}
              sx={{ textDecorationLine: 'underline', cursor: 'pointer' }}
              onClick={onOpenSpecialRequestPopup}
            >
              {t('style_selection_order_step:special_request.tell_us')}
            </Typography>

          </Stack>

        </Fragment>
      }

    </Stack>
  )
}
